import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { StatCard, Loading } from "../components/shared";
import { formatINRShort, formatINR } from "../lib/format";
import { DATE_RANGES } from "../lib/constants";
import { Button } from "../components/ui/button";
import {
  Wallet, Landmark, Coins, ArrowDownLeft, TrendingUp, ArrowUpRight, HandCoins, Building2,
  Undo2, PiggyBank, Users, Receipt, Sparkles, Hash, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
  LineChart, Line, Legend,
} from "recharts";

const COLORS = ["#059669", "#2563EB", "#D97706", "#7C3AED", "#0D9488", "#DB2777", "#4F46E5", "#E11D48"];

function ChartCard({ title, children }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
      <h3 className="font-heading text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState("this_month");
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/dashboard?range=${range}`),
        api.get("/ai/daily-summary").catch(() => ({ data: null })),
      ]);
      setData(d.data); setAi(a.data);
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  if (loading || !data) return <Loading />;
  const k = data.kpis;
  const c = data.charts;
  const goTxn = (q) => navigate(`/transactions?${q}`);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-500">Financial overview · {data.period.from} to {data.period.to}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DATE_RANGES.filter((r) => r.key !== "custom").map((r) => (
            <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${range === r.key ? "bg-[#0F2942] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>{r.label}</button>
          ))}
        </div>
      </div>

      {/* AI Summary */}
      {ai?.summary && (
        <div className="bg-gradient-to-r from-[#0F2942] to-[#163a5c] text-white rounded-xl p-4 mb-5 flex gap-3 items-start shadow-md">
          <div className="h-9 w-9 rounded-lg bg-teal-500/20 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-teal-300" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-teal-300 mb-1">AI Daily Summary</div>
            <p className="text-sm text-slate-100 leading-relaxed" data-testid="ai-daily-summary">{ai.summary}</p>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-5">
        {[["receive", "Receive Payment", Receipt], ["expense", "Add Expense", Wallet], ["advance_candidate", "Candidate Advance", HandCoins], ["give_advance", "Give Advance", ArrowUpRight], ["refund", "Refund", Undo2], ["transfer", "Transfer", ArrowUpRight], ["add_candidate", "Add Candidate", Users]].map(([t, l, I]) => (
          <Button key={t} variant="outline" size="sm" onClick={() => open(t)} data-testid={`qa-${t}`} className="gap-1.5 bg-white dark:bg-slate-900">
            <I className="h-3.5 w-3.5 text-teal-600" /> {l}
          </Button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
        <StatCard testid="kpi-cash" label="Total Cash Balance" value={k.total_cash} icon={Coins} accent="emerald" onClick={() => navigate("/accounts")} />
        <StatCard testid="kpi-bank" label="Total Bank Balance" value={k.total_bank} icon={Landmark} accent="blue" onClick={() => navigate("/accounts")} />
        <StatCard testid="kpi-total" label="Total Account Balance" value={k.total_balance} icon={Wallet} accent="teal" onClick={() => navigate("/accounts")} />
        <StatCard testid="kpi-txn-count" label="Transactions Today" value={k.today_txn_count} isCurrency={false} icon={Hash} accent="slate" onClick={() => goTxn(`date_from=${today}&date_to=${today}`)} />
        <StatCard testid="kpi-today-received" label="Today Money Received" value={k.today_received} icon={ArrowDownLeft} accent="emerald" onClick={() => goTxn(`date_from=${today}&date_to=${today}`)} />
        <StatCard testid="kpi-today-income" label="Today Recognized Income" value={k.today_income} icon={TrendingUp} accent="blue" />
        <StatCard testid="kpi-today-expense" label="Today Expenses" value={k.today_expense} icon={Wallet} accent="amber" onClick={() => goTxn(`type=expense&date_from=${today}&date_to=${today}`)} />
        <StatCard testid="kpi-today-advance" label="Today Candidate Advances" value={k.today_candidate_advance} icon={HandCoins} accent="violet" onClick={() => goTxn(`type=candidate_advance&date_from=${today}&date_to=${today}`)} />
        <StatCard testid="kpi-today-agency" label="Today Agency Payments" value={k.today_agency_payments} icon={Building2} accent="rose" />
        <StatCard testid="kpi-today-refunds" label="Today Refunds" value={k.today_refunds} icon={Undo2} accent="rose" onClick={() => goTxn(`type=refund&date_from=${today}&date_to=${today}`)} />
        <StatCard testid="kpi-month-income" label="Month Income" value={k.month_income} icon={TrendingUp} accent="blue" />
        <StatCard testid="kpi-month-expense" label="Month Expenses" value={k.month_expense} icon={Wallet} accent="amber" />
        <StatCard testid="kpi-month-profit" label="Month Net Profit" value={k.month_profit} icon={PiggyBank} accent={k.month_profit >= 0 ? "emerald" : "rose"} />
        <StatCard testid="kpi-outstanding" label="Candidate Outstanding" value={k.candidate_outstanding} icon={Users} accent="amber" onClick={() => navigate("/reports/outstanding")} />
        <StatCard testid="kpi-adv-held" label="Candidate Advances Held" value={k.candidate_advances_held} icon={HandCoins} accent="violet" onClick={() => navigate("/advances")} />
        <StatCard testid="kpi-adv-paid" label="Advances Paid (Agencies)" value={k.advances_paid} icon={ArrowUpRight} accent="violet" onClick={() => navigate("/advances")} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <ChartCard title="Received vs Income vs Expense">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[{ name: "Received", v: c.income_vs_expense.received }, { name: "Income", v: c.income_vs_expense.income }, { name: "Expense", v: c.income_vs_expense.expense }, { name: "Paid", v: c.income_vs_expense.paid }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tickFormatter={formatINRShort} tick={{ fontSize: 10 }} width={55} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                {["#059669", "#2563EB", "#D97706", "#DC2626"].map((col, i) => <Cell key={i} fill={col} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Daily Cash Flow (14 days)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={c.daily_cashflow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10 }} /><YAxis tickFormatter={formatINRShort} tick={{ fontSize: 10 }} width={45} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Line type="monotone" dataKey="in" stroke="#059669" strokeWidth={2} dot={false} name="In" />
              <Line type="monotone" dataKey="out" stroke="#DC2626" strokeWidth={2} dot={false} name="Out" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Expense by Category">
          {c.expense_by_category.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={c.expense_by_category.slice(0, 8)} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name.slice(0, 10)}>
                  {c.expense_by_category.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-sm text-slate-400 py-16">No expenses in period</div>}
        </ChartCard>
        <ChartCard title="Income by Category">
          {c.income_by_category.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={c.income_by_category.slice(0, 8)} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name.slice(0, 10)}>
                  {c.income_by_category.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-sm text-slate-400 py-16">No recognized income</div>}
        </ChartCard>
        <ChartCard title="Account-wise Balance">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={c.accounts} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tickFormatter={formatINRShort} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Bar dataKey="balance" fill="#0D9488" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Branch-wise (period)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={c.branches}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tickFormatter={(n) => n.replace(/.*- /, "")} tick={{ fontSize: 10 }} /><YAxis tickFormatter={formatINRShort} tick={{ fontSize: 10 }} width={45} />
              <Tooltip formatter={(v) => formatINR(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="income" fill="#2563EB" radius={[4, 4, 0, 0]} name="Income" />
              <Bar dataKey="expense" fill="#D97706" radius={[4, 4, 0, 0]} name="Expense" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
